import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false, // Set to true if your server requires credentials
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    console.log('🔑 Axios Interceptor - Token:', token ? 'Token exists' : 'No token');
    console.log('📍 Request URL:', config.url);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Authorization header added to request');
    } else {
      console.log('⚠️ No token found, request will be sent without Authorization header');
    }
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // List of endpoints that should not redirect on 401
      const noRedirectEndpoints = [
        '/auth/login',
        '/auth/register',
        '/auth/select-plan',
        '/auth/payment-details',
        '/auth/organization-info',
        '/auth/personal-info',
        '/auth/preferences'
      ];

      // Check if the current request should not redirect
      const shouldNotRedirect = noRedirectEndpoints.some(endpoint =>
        error.config?.url?.includes(endpoint)
      );

      if (shouldNotRedirect) {
        // Just reject the promise without redirecting
        return Promise.reject(error);
      }

      // For other requests, clear token and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Fallback fetch method for CORS issues
const fetchAPI = async (endpoint, options = {}) => {
  // Only use the configured API URL, don't try localhost in production
  const baseURL = process.env.REACT_APP_API_URL;
  
  if (!baseURL) {
    throw new Error('API URL is not configured. Please set REACT_APP_API_URL environment variable.');
  }

  const url = `${baseURL}${endpoint}`;
  
  console.log(`Making API call to: ${url}`);
  
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    mode: 'cors',
    ...options,
  };

  // Add auth token if available
  const token = localStorage.getItem('authToken');
  if (token) {
    defaultOptions.headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Server error' }));
      const error = new Error(errorData.message || `HTTP ${response.status}`);
      error.response = {
        status: response.status,
        data: errorData
      };
      throw error;
    }
    
    console.log(`✅ API call successful: ${url}`);
    return await response.json();
  } catch (error) {
    console.error(`❌ API call failed:`, error.message);
    throw error;
  }
};

export const authService = {
  // Login user
  login: async (credentials) => {
    try {
      // Validate input
      if (!credentials || !credentials.email || !credentials.password) {
        throw new Error('Email and password are required');
      }

      // Try axios first, fallback to fetch if CORS issues
      let response;

      try {
        response = await api.post('/auth/login', {
          email: credentials.email,
          password: credentials.password,
        });
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        
        // If it's a 401 or other HTTP error, don't try fetch
        if (axiosError.response && axiosError.response.status) {
          throw axiosError; // Re-throw HTTP errors
        }
        
        // Only try fetch for network/CORS errors
        try {
          response = await fetchAPI('/auth/login', {
            method: 'POST',
            body: JSON.stringify({
              email: credentials.email,
              password: credentials.password,
            }),
          });
        } catch (fetchError) {
          // If both axios and fetch fail, throw the original axios error
          throw axiosError;
        }
      }

      // Validate response
      if (!response) {
        throw new Error('No response received from server');
      }

      // Store token and user data only if login was successful
      const token = response.token || response.access_token;
      if (token && response.user) {
        try {
          localStorage.setItem('authToken', token);
          localStorage.setItem('user', JSON.stringify(response.user));
        } catch (storageError) {
          console.error('Failed to store auth data:', storageError);
          // Continue anyway, as the login was successful
        }
      } else if (response.requiresMfa) {
        return response;
      } else if (response.success === false) {
        throw new Error(response.message || 'Login failed');
      }

      return response;
    } catch (error) {
      console.error('Login error:', error);
      
      // Enhance error information
      if (error.response) {
        // Axios error with response
        const enhancedError = new Error(error.response.data?.message || 'Login failed');
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        throw enhancedError;
      } else if (error.message) {
        // Already has a message
        throw error;
      } else {
        // Unknown error
        throw new Error('An unexpected error occurred during login');
      }
    }
  },

  verifyMfa: async ({ mfaToken, otpCode }) => {
    const response = await api.post('/auth/mfa/verify', { mfaToken, otpCode });
    const data = response.data;
    const token = data.token || data.access_token;
    if (token && data.user) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  enableMfa: async () => {
    const response = await api.post('/auth/mfa/enable', { enabled: true });
    return response.data;
  },

  disableMfa: async () => {
    const response = await api.post('/auth/mfa/disable');
    return response.data;
  },

  // Register user
  register: async (userData) => {
    try {
      const requestData = {
        name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
      };

      console.log('🚀 Starting registration with data:', { ...requestData, password: '[HIDDEN]' });

      // Try axios first, fallback to fetch only for network/CORS errors (not HTTP errors)
      let response;
      try {
        console.log('📡 Making registration request...');
        response = await api.post('/auth/register', requestData);
        response = response.data;
        console.log('✅ Registration successful');
      } catch (axiosError) {
        // If it's an HTTP error (400, 409, 500, etc.), don't try fetch - just re-throw
        if (axiosError.response && axiosError.response.status) {
          console.error('❌ Registration failed with HTTP error:', axiosError.response.status);
          throw axiosError; // Re-throw HTTP errors immediately
        }
        
        // Only try fetch for network/CORS errors (no response from server)
        console.log('❌ Network error, trying fetch fallback...', axiosError.message);
        try {
          response = await fetchAPI('/auth/register', {
            method: 'POST',
            body: JSON.stringify(requestData),
          });
          console.log('✅ Fetch request successful');
        } catch (fetchError) {
          // If both fail, throw the original axios error
          throw axiosError;
        }
      }

      return response;
    } catch (error) {
      console.error('💥 Registration error:', error);
      throw error;
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    try {
      const requestData = { email: email };

      // Try axios first, fallback to fetch if CORS issues
      let response;
      try {
        response = await api.post('/auth/forgot-password', requestData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/forgot-password', {
          method: 'POST',
          body: JSON.stringify(requestData),
        });
      }

      return response;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  },

  // Reset password
  resetPassword: async (token, newPassword) => {
    try {
      const requestData = {
        token: token,
        newPassword: newPassword,
      };

      // Try axios first, fallback to fetch only for network errors
      let response;
      try {
        response = await api.post('/auth/reset-password', requestData);
        response = response.data;
      } catch (axiosError) {
        // If it's a 400/401/404 error, don't try fetch - just throw the error
        if (axiosError.response && axiosError.response.status) {
          throw axiosError; // Re-throw HTTP errors
        }
        
        // Only try fetch for network/CORS errors
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify(requestData),
        });
      }

      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      
      // Enhance error information
      if (error.response) {
        // Axios error with response
        const enhancedError = new Error(error.response.data?.message || 'Password reset failed');
        enhancedError.response = error.response;
        enhancedError.status = error.response.status;
        throw enhancedError;
      } else if (error.message) {
        // Already has a message
        throw error;
      } else {
        // Unknown error
        throw new Error('An unexpected error occurred during password reset');
      }
    }
  },

  // Generate OTP
  generateOTP: async (email) => {
    try {
      const requestData = { email: email };

      // Try axios first, fallback to fetch if CORS issues
      let response;
      try {
        response = await api.post('/auth/generate-otp', requestData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/generate-otp', {
          method: 'POST',
          body: JSON.stringify(requestData),
        });
      }

      return response;
    } catch (error) {
      console.error('Generate OTP error:', error);
      throw error;
    }
  },

  // Verify OTP
  verifyOTP: async (otp, email) => {
    try {
      const requestData = {
        otp: otp,
        email: email,
      };

      // Try axios first, fallback to fetch if CORS issues
      let response;
      try {
        response = await api.post('/auth/verify-otp', requestData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/verify-otp', {
          method: 'POST',
          body: JSON.stringify(requestData),
        });
      }

      // Store token if verification includes login
      if (response.token) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    }
  },

  // Verify email
  verifyEmail: async (token) => {
    try {
      // Try axios first, fallback to fetch if CORS issues
      let response;
      try {
        response = await api.get(`/auth/verify-email?token=${token}`);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI(`/auth/verify-email?token=${token}`, {
          method: 'GET',
        });
      }

      return response;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  },

  // Resend verification email
  resendVerification: async (email) => {
    try {
      const requestData = { email: email };

      // Try axios first, fallback to fetch if CORS issues
      let response;
      try {
        response = await api.post('/auth/resend-verification', requestData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/resend-verification', {
          method: 'POST',
          body: JSON.stringify(requestData),
        });
      }

      return response;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  },


  // Logout user
  logout: async () => {
    try {
      // Clear local storage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Clear onboarding data from localStorage on logout
      localStorage.removeItem('onboardingOrganization');
      localStorage.removeItem('onboardingPersonal');
      localStorage.removeItem('onboardingPreferences');
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  // Get current user
  getCurrentUser: () => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('user');
    return !!(token && user);
  },

  // Select plan
  selectPlan: async (planData) => {
    try {
      let response;
      try {
        response = await api.post('/auth/select-plan', planData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/select-plan', {
          method: 'POST',
          body: JSON.stringify(planData),
        });
      }
      return response;
    } catch (error) {
      console.error('Select plan error:', error);
      throw error;
    }
  },

  // Submit payment details
  submitPaymentDetails: async (paymentData) => {
    try {
      let response;
      try {
        response = await api.post('/auth/payment-details', paymentData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/payment-details', {
          method: 'POST',
          body: JSON.stringify(paymentData),
        });
      }
      return response;
    } catch (error) {
      console.error('Payment details error:', error);
      throw error;
    }
  },

  // Submit organization info
  submitOrganizationInfo: async (organizationData) => {
    try {
      let response;
      try {
        response = await api.post('/auth/organization-info', organizationData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/organization-info', {
          method: 'POST',
          body: JSON.stringify(organizationData),
        });
      }
      return response;
    } catch (error) {
      console.error('Organization info error:', error);
      throw error;
    }
  },

  // Submit personal info
  submitPersonalInfo: async (personalData) => {
    try {
      let response;
      try {
        response = await api.post('/auth/personal-info', personalData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/personal-info', {
          method: 'POST',
          body: JSON.stringify(personalData),
        });
      }
      return response;
    } catch (error) {
      console.error('Personal info error:', error);
      throw error;
    }
  },

  // Submit preferences
  submitPreferences: async (preferencesData) => {
    try {
      let response;
      try {
        response = await api.post('/auth/preferences', preferencesData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/preferences', {
          method: 'POST',
          body: JSON.stringify(preferencesData),
        });
      }
      return response;
    } catch (error) {
      console.error('Preferences error:', error);
      throw error;
    }
  },

  // Get onboarding status
  getOnboardingStatus: async () => {
    try {
      let response;
      try {
        response = await api.get('/auth/onboarding-status');
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/onboarding-status', {
          method: 'GET',
        });
      }
      return response;
    } catch (error) {
      console.error('Get onboarding status error:', error);
      throw error;
    }
  },

  // Get organization info
  getOrganizationInfo: async () => {
    try {
      let response;
      try {
        response = await api.get('/auth/organization-info');
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/organization-info', {
          method: 'GET',
        });
      }
      return response;
    } catch (error) {
      console.error('Get organization info error:', error);
      throw error;
    }
  },

  // Get personal info
  getPersonalInfo: async () => {
    try {
      let response;
      try {
        response = await api.get('/auth/personal-info');
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/personal-info', {
          method: 'GET',
        });
      }
      return response;
    } catch (error) {
      console.error('Get personal info error:', error);
      throw error;
    }
  },

  // Get preferences
  getPreferences: async () => {
    try {
      let response;
      try {
        response = await api.get('/auth/preferences');
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/preferences', {
          method: 'GET',
        });
      }
      return response;
    } catch (error) {
      console.error('Get preferences error:', error);
      throw error;
    }
  },

  // Create Stripe checkout session
  createCheckoutSession: async (checkoutData) => {
    try {
      let response;
      try {
        response = await api.post('/auth/create-checkout-session', checkoutData);
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/create-checkout-session', {
          method: 'POST',
          body: JSON.stringify(checkoutData),
        });
      }
      return response;
    } catch (error) {
      console.error('Create checkout session error:', error);
      throw error;
    }
  },

  // Handle payment success
  handlePaymentSuccess: async (sessionId) => {
    try {
      let response;
      try {
        response = await api.post('/auth/payment-success', { sessionId });
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/payment-success', {
          method: 'POST',
          body: JSON.stringify({ sessionId }),
        });
      }
      return response;
    } catch (error) {
      console.error('Payment success error:', error);
      throw error;
    }
  },

  // Get user profile (fresh data from API)
  getProfile: async () => {
    try {
      let response;
      try {
        response = await api.get('/auth/profile');
        response = response.data;
      } catch (axiosError) {
        console.log('Axios failed, trying fetch...', axiosError.message);
        response = await fetchAPI('/auth/profile', {
          method: 'GET',
        });
      }

      // Update localStorage with fresh user data
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  },

  updateNotificationPrefs: async (prefs) => {
    const response = await api.patch('/auth/notification-prefs', prefs);
    return response.data;
  },

  updateAppPreferences: async (prefs) => {
    const response = await api.patch('/auth/app-preferences', prefs);
    return response.data;
  },

  getSystemHealth: async () => {
    const response = await api.get('/auth/system-health');
    return response.data;
  },
};

export default authService;
